package org.teddapp.project.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.ui.unit.sp
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.layout.ParentDataModifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog

/**
 * En-tête d'écran : un sur-titre facultatif en mono (date, contexte), le titre, puis les
 * actions à droite. [large] réserve le grand titre à l'écran d'accueil.
 */
@Composable
fun TeddScreenHeader(
    title: String,
    modifier: Modifier = Modifier,
    overline: String? = null,
    subtitle: String? = null,
    large: Boolean = false,
    actions: @Composable RowScope.() -> Unit = {},
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Bottom,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            if (overline != null) {
                Text(overline.uppercase(), color = TeddTokens.InkMuted, style = TeddType.MonoSm, maxLines = 1)
            }
            TeddFitText(
                text = title,
                style = if (large) TeddType.DisplayLg else TeddType.Title1,
                minFontSize = if (large) 24.sp else 18.sp,
                maxLines = 2,
            )
            if (subtitle != null) Text(subtitle, color = TeddTokens.InkMuted, style = TeddType.Body)
        }
        actions()
    }
}

/**
 * État vide : une phrase qui dit quoi faire, et un bouton. Jamais une illustration,
 * jamais un simple « Rien ici ».
 */
@Composable
fun TeddEmptyState(
    title: String,
    modifier: Modifier = Modifier,
    message: String? = null,
    icon: String? = null,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null,
) {
    Column(
        modifier = modifier.padding(TeddSpace.S8),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        if (icon != null) {
            Box(
                Modifier.size(44.dp).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)),
                contentAlignment = Alignment.Center,
            ) { TeddIcon(name = icon, size = 20.dp, color = TeddTokens.InkMuted) }
            Spacer(Modifier.height(TeddSpace.S1))
        }
        Text(title, color = TeddTokens.Ink, style = TeddType.Title2, textAlign = TextAlign.Center)
        if (message != null) {
            Text(
                message,
                modifier = Modifier.widthIn(max = 420.dp),
                color = TeddTokens.InkMuted,
                style = TeddType.Body,
                textAlign = TextAlign.Center,
            )
        }
        if (actionLabel != null && onAction != null) {
            Spacer(Modifier.height(TeddSpace.S2))
            TeddButton(actionLabel, onAction, kind = TeddButtonKind.Primary)
        }
    }
}

/**
 * Panneau latéral : détail ou formulaire qui glisse depuis la droite, au-dessus d'un voile.
 * Une seule largeur dans toute l'application. À poser à la racine de l'écran, dans une
 * `Box` qui couvre le contenu. Échap et un clic sur le voile le ferment.
 */
@Composable
fun BoxScope.TeddSidePanel(
    visible: Boolean,
    onDismiss: () -> Unit,
    title: String,
    width: Dp = TeddSize.SidePanel,
    footer: (@Composable RowScope.() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit,
) {
    AnimatedVisibility(visible, enter = fadeIn(tween(150)), exit = fadeOut(tween(150))) {
        Box(
            Modifier
                .fillMaxSize()
                .background(Color(0x66000000))
                .clickable(remember { MutableInteractionSource() }, indication = null, onClick = onDismiss),
        )
    }
    AnimatedVisibility(
        visible,
        modifier = Modifier.align(Alignment.CenterEnd),
        enter = slideInHorizontally(tween(220)) { it },
        exit = slideOutHorizontally(tween(180)) { it },
    ) {
        Column(
            Modifier
                .width(width)
                .fillMaxHeight()
                .background(TeddTokens.SurfaceOverlay)
                .border(1.dp, TeddTokens.Line)
                // Un clic dans le panneau ne doit pas atteindre le voile.
                .clickable(remember { MutableInteractionSource() }, indication = null, onClick = {}),
        ) {
            Row(
                Modifier.fillMaxWidth().padding(start = TeddSpace.S6, end = TeddSpace.S4, top = TeddSpace.S5, bottom = TeddSpace.S4),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(title, Modifier.weight(1f), color = TeddTokens.Ink, style = TeddType.Title2, maxLines = 1, overflow = TextOverflow.Ellipsis)
                TeddIconButton(onClick = onDismiss) { TeddIcon("x", size = 18.dp, color = TeddTokens.InkMuted) }
            }
            Column(Modifier.weight(1f).padding(horizontal = TeddSpace.S6), content = content)
            if (footer != null) {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .background(TeddTokens.SurfaceSunken)
                        .padding(horizontal = TeddSpace.S6, vertical = TeddSpace.S4),
                    horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2, Alignment.End),
                    verticalAlignment = Alignment.CenterVertically,
                    content = footer,
                )
            }
        }
    }
}

/**
 * Confirmation d'une action qui ne se rattrape pas. Le bouton dit ce qu'il fait
 * (« Supprimer la note »), jamais « OK ».
 */
@Composable
fun TeddConfirmDialog(
    title: String,
    message: String,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    danger: Boolean = true,
    cancelLabel: String = "Annuler",
) {
    Dialog(onDismissRequest = onDismiss) {
        val shape = RoundedCornerShape(TeddTokens.RLg)
        Column(
            Modifier
                .width(400.dp)
                .teddOverlayShadow(shape)
                .background(TeddTokens.SurfaceOverlay, shape)
                .border(1.dp, TeddTokens.Line, shape)
                .padding(TeddSpace.S6),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            Text(title, color = TeddTokens.Ink, style = TeddType.Title2)
            Text(message, color = TeddTokens.InkMuted, style = TeddType.Body)
            Row(
                Modifier.fillMaxWidth().padding(top = TeddSpace.S2),
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2, Alignment.End),
            ) {
                TeddButton(cancelLabel, onDismiss, kind = TeddButtonKind.Ghost)
                TeddButton(
                    confirmLabel,
                    onConfirm,
                    kind = if (danger) TeddButtonKind.Danger else TeddButtonKind.Primary,
                    icon = if (danger) "trash" else null,
                )
            }
        }
    }
}

// ── Grille bento ───────────────────────────────────────────────────────

private class BentoSpan(val span: Int) : ParentDataModifier {
    override fun Density.modifyParentData(parentData: Any?) = this@BentoSpan
}

/** Nombre de colonnes (sur 12) qu'occupe une tuile de [TeddBento]. */
fun Modifier.bentoSpan(span: Int): Modifier = this.then(BentoSpan(span.coerceIn(1, 12)))

/**
 * Grille bento de 12 colonnes. Chaque enfant dit sa largeur par [bentoSpan] ; les tuiles
 * d'une même rangée prennent la hauteur de la plus haute.
 */
@Composable
fun TeddBento(
    modifier: Modifier = Modifier,
    gutter: Dp = TeddSpace.S4,
    content: @Composable () -> Unit,
) {
    Layout(content = content, modifier = modifier) { measurables, constraints ->
        val gap = gutter.roundToPx()
        val column = (constraints.maxWidth - gap * 11) / 12f
        fun widthOf(span: Int) = (column * span + gap * (span - 1)).toInt()

        // Répartition en rangées de 12 colonnes.
        val rows = mutableListOf<MutableList<Int>>()
        var used = 12
        measurables.forEachIndexed { index, measurable ->
            val span = (measurable.parentData as? BentoSpan)?.span ?: 12
            if (used + span > 12) {
                rows.add(mutableListOf())
                used = 0
            }
            rows.last().add(index)
            used += span
        }

        val placeables = arrayOfNulls<androidx.compose.ui.layout.Placeable>(measurables.size)
        val rowHeights = rows.map { row ->
            val height = row.maxOf { index ->
                val span = (measurables[index].parentData as? BentoSpan)?.span ?: 12
                measurables[index].minIntrinsicHeight(widthOf(span))
            }
            row.forEach { index ->
                val span = (measurables[index].parentData as? BentoSpan)?.span ?: 12
                placeables[index] = measurables[index].measure(Constraints.fixed(widthOf(span), height))
            }
            height
        }
        val total = rowHeights.sum() + gap * (rows.size - 1).coerceAtLeast(0)
        layout(constraints.maxWidth, total) {
            var y = 0
            rows.forEachIndexed { rowIndex, row ->
                var x = 0
                row.forEach { index ->
                    val placeable = placeables[index]!!
                    placeable.placeRelative(x, y)
                    x += placeable.width + gap
                }
                y += rowHeights[rowIndex] + gap
            }
        }
    }
}
